import flagUs from "../assets/flags/us.png";
import flagCa from "../assets/flags/ca.png";
import flagMx from "../assets/flags/mx.png";
import flagIn from "../assets/flags/in.png";

export type PhoneCountryCode = "US" | "CA" | "MX" | "IN";

export type PhoneCountryOption = {
    code: PhoneCountryCode;
    name: string;
    dialCode: string;
    /** Local circular flag asset URL. */
    flagIcon: string;
    /** Max national digits (without country code). */
    maxDigits: number;
    placeholder: string;
};

/** Phone country options for the country dropdown. */
export const PHONE_COUNTRIES: PhoneCountryOption[] = [
    {
        code: "US",
        name: "United States",
        dialCode: "+1",
        flagIcon: flagUs,
        maxDigits: 10,
        placeholder: "303-555-0123",
    },
    {
        code: "CA",
        name: "Canada",
        dialCode: "+1",
        flagIcon: flagCa,
        maxDigits: 10,
        placeholder: "416-555-0123",
    },
    {
        code: "MX",
        name: "Mexico",
        dialCode: "+52",
        flagIcon: flagMx,
        maxDigits: 10,
        placeholder: "55-1234-5678",
    },
    {
        code: "IN",
        name: "India",
        dialCode: "+91",
        flagIcon: flagIn,
        maxDigits: 10,
        placeholder: "98765-43210",
    },
];

export const DEFAULT_PHONE_COUNTRY: PhoneCountryCode = "US";

export function getPhoneCountry(
    code: string | null | undefined,
): PhoneCountryOption {
    return (
        PHONE_COUNTRIES.find((country) => country.code === code) ||
        PHONE_COUNTRIES[0]
    );
}

/** Strip non-digits from a phone string. */
export function phoneDigitsOnly(value: string): string {
    return (value || "").replace(/\D/g, "");
}

/**
 * Format a national phone number while typing.
 * US / CA → XXX-XXX-XXXX
 * MX → XX-XXXX-XXXX
 * IN → XXXXX-XXXXX
 */
export function formatPhoneNumber(
    value: string,
    countryCode: string = DEFAULT_PHONE_COUNTRY,
): string {
    const country = getPhoneCountry(countryCode);
    const digits = phoneDigitsOnly(value).slice(0, country.maxDigits);

    if (country.code === "MX") {
        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 6) {
            return `${digits.slice(0, 2)}-${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    if (country.code === "IN") {
        if (digits.length <= 5) {
            return digits;
        }
        return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }

    // US / CA (NANP)
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** @deprecated Prefer formatPhoneNumber */
export function formatUsPhone(value: string): string {
    return formatPhoneNumber(value, "US");
}

export function isValidPhoneForCountry(
    value: string,
    countryCode: string = DEFAULT_PHONE_COUNTRY,
): boolean {
    const country = getPhoneCountry(countryCode);
    return phoneDigitsOnly(value).length === country.maxDigits;
}

/** @deprecated Prefer isValidPhoneForCountry */
export function isValidUsPhone(value: string): boolean {
    return isValidPhoneForCountry(value, "US");
}

export function formatPhoneWithDialCode(
    value: string,
    countryCode: string = DEFAULT_PHONE_COUNTRY,
): string {
    const country = getPhoneCountry(countryCode);
    const formatted = formatPhoneNumber(value, countryCode);
    if (!formatted) {
        return "—";
    }
    return `${country.dialCode} ${formatted}`;
}
